import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Phase 3A · Admin dashboard nudge — surfaces unrecovered event-log rows.
 * Polls /api/admin/event-outbox/summary; renders nothing when clean.
 */
export default function EventOutboxNudge() {
  const [summary, setSummary] = useState({ pending_count: 0, failure_count: 0 });
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/event-outbox/summary");
      setSummary(r.data || {});
      if ((r.data?.failure_count || 0) > 0 || (r.data?.pending_count || 0) > 0) {
        const l = await api.get("/admin/event-outbox");
        setRows(l.data || []);
      }
    } catch (_e) {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const failures = summary.failure_count || 0;
  const pending = summary.pending_count || 0;
  if (failures === 0 && pending === 0) return null;

  return (
    <div
      className={`mb-6 border rounded-lg p-4 ${
        failures > 0
          ? "border-red-300 bg-red-50"
          : "border-amber-300 bg-amber-50"
      }`}
      data-testid="admin-event-outbox-nudge"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${failures > 0 ? "text-red-800" : "text-amber-800"}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-[13px] font-semibold ${failures > 0 ? "text-red-900" : "text-amber-900"}`}>
            Activity log:{" "}
            {failures > 0 && (
              <span data-testid="nudge-failure-count">{failures} unrecovered event{failures === 1 ? "" : "s"}</span>
            )}
            {failures > 0 && pending > 0 && " · "}
            {pending > 0 && <span data-testid="nudge-pending-count">{pending} retrying</span>}
          </div>
          <div className="text-[12px] text-charcoal/70 mt-0.5">
            Some order-event log writes couldn't be committed. Retries are ongoing; permanent failures need admin review.
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 text-[10px] uppercase tracking-widest text-charcoal/60 hover:text-oxblood"
            data-testid="nudge-toggle"
          >
            {expanded ? "Hide" : "Review"}
          </button>
          {expanded && rows.length > 0 && (
            <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto" data-testid="nudge-rows">
              {rows.map((r) => (
                <div
                  key={r.outbox_id}
                  className="flex items-center gap-2 text-[11px] font-mono text-charcoal/85"
                >
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      r.state === "permanent_failure" ? "bg-red-600" : "bg-amber-500"
                    }`}
                  />
                  <span className="uppercase tracking-widest text-[9px]">{r.state}</span>
                  <span>·</span>
                  <span>{r.event_type}</span>
                  {r.order_number && (
                    <Link
                      to={`/admin/orders/${r.order_id}`}
                      className="ml-1 underline decoration-charcoal/30 hover:text-oxblood"
                    >
                      {r.order_number}
                    </Link>
                  )}
                  <span className="opacity-70 ml-auto">
                    {r.attempts > 0 && `${r.attempts} attempts`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
